import React, { useState, useEffect } from "react";
import { useProperty } from "../context/PropertyContext";
import PropTypes from "prop-types";
import { IP } from '../configuracion.js';
import { format, addDays, parse } from "date-fns";
import "../styles/fechacontrol.css";

const FORMATO_FECHA = "dd/MM/yyyy";

const ControlFecha = ({ show, onClose, onChequeSeleccionado }) => {
  const { property } = useProperty();
  const [cheques, setCheques] = useState([]);
  const [fechaControl, setFechaControl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [enviandoWhatsApp, setEnviandoWhatsApp] = useState(false);
  const [realizandoBackup, setRealizandoBackup] = useState(false);
  const [progresoWhatsApp, setProgresoWhatsApp] = useState({
    total: 0,
    enviados: 0,
    fallidos: 0,
    actual: '',
    estado: ''
  });

  const [datosCorte, setDatosCorte] = useState(null);

  useEffect(() => {
    const cargarDatos = async () => {
      try {
        setLoading(true);

        if (!property) {
          throw new Error("Propiedad no definida.");
        }

        const [chequesRes, fechaRes] = await Promise.all([
          fetch(`${IP}/tavpos/backend/lectura_cheques.php?property=${encodeURIComponent(property)}`),
          fetch(`${IP}/tavpos/backend/close_day_fechas.php?property=${encodeURIComponent(property)}`),
        ]);

        if (!chequesRes.ok) throw new Error(`Error al cargar cheques`);
        const chequesData = await chequesRes.json();
        if (!chequesData.success) throw new Error(chequesData.message);

        if (!fechaRes.ok) throw new Error(`Error al cargar fecha control`);
        const fechaData = await fechaRes.json();
        if (!fechaData.success) throw new Error(fechaData.message);

        const chequesAbiertos = chequesData.data.filter((c) => !c.fecha_cierre || c.fecha_cierre === null || c.fecha_cierre === "" || c.fecha_cierre === "0000-00-00");
        setCheques(chequesAbiertos);
        
        setFechaControl(fechaData.data.detalles);
        await cargarDatosCorteCompleto(fechaData.data.detalles);

      } catch (error) {
        console.error("Error:", error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    if (show) cargarDatos();
  }, [show, property]);

  const cargarDatosCorteCompleto = async (fechaControlData) => {
    try {
      if (!fechaControlData?.fecha_trabajo) return;

      const fechaTrabajo = new Date(fechaControlData.fecha_trabajo);
      const fechaCierre = addDays(fechaTrabajo, 1);
      const fechaCierreStr = format(fechaCierre, "yyyy-MM-dd");
      
      console.log("📅 Cargando datos para fecha de cierre:", fechaCierreStr);

      const [corteResponse, salesResponse] = await Promise.all([
        fetch(`${IP}/tavpos/backend/lectura_cheques_corte.php?property=${encodeURIComponent(property)}`),
        fetch(`${IP}/tavpos/backend/lectura_cheques.php?property=${encodeURIComponent(property)}`)
      ]);

      if (!corteResponse.ok || !salesResponse.ok) return;

      const corteData = await corteResponse.json();
      const salesData = await salesResponse.json();

      if (corteData.success && salesData.success) {
        const chequesCorte = (corteData.data?.cheques || [])
          .filter(cheque => cheque.fecha_control)
          .map(cheque => ({
            ...cheque,
            fecha_control: format(parse(cheque.fecha_control, 'yyyy-MM-dd', new Date()), 'yyyy-MM-dd')
          }));
        
        const chequesVentas = (salesData.data || [])
          .filter(cheque => cheque.fecha_control)
          .map(cheque => ({
            ...cheque,
            fecha_control: format(parse(cheque.fecha_control, 'yyyy-MM-dd', new Date()), 'yyyy-MM-dd')
          }));

        const filteredChequesCorte = chequesCorte.filter(cheque => cheque.fecha_control === fechaCierreStr);
        const filteredChequesVentas = chequesVentas.filter(cheque => cheque.fecha_control === fechaCierreStr);

        // Requerimiento: comparar la venta de hoy contra la de ayer. chequesCorte
        // ya trae varios días (por eso se filtra arriba por fechaCierreStr), así
        // que se reutiliza el mismo array para sacar el día anterior sin pedir
        // nada nuevo al backend.
        const fechaAyerStr = format(addDays(fechaCierre, -1), "yyyy-MM-dd");
        const totalVentasAyer = chequesCorte
          .filter(cheque => cheque.fecha_control === fechaAyerStr)
          .reduce((sum, cheque) => sum + (parseFloat(cheque.total_cheque) || 0), 0);

        console.log(`📋 Cheques: ${filteredChequesCorte.length} corte, ${filteredChequesVentas.length} ventas`);

        if (filteredChequesCorte.length === 0 && filteredChequesVentas.length === 0) {
          setDatosCorte(null);
          return;
        }

        // Variables principales
        let totalVentas = 0;
        let totalCobros = 0;
        let totalPropinas = 0;
        let totalCambio = 0;
        let totalImpuestos = 0;
        let totalSinImpuestos = 0;
        let totalDescuentos = 0;
        let totalFueraDeBalance = 0;
        const paymentMethods = {};
        let unbalancedCheques = [];
        
        // NUEVAS VARIABLES PARA REVENUE Y CLIENTES
        const revenueMap = new Map(); // revenue -> { totalVentas, totalCheques, cheques: [] }
        let totalClientes = 0;
        let sumaCheques = 0;
        const clientesMap = new Map(); // Para contar clientes únicos

        // Crear mapa de ventas por ID de cheque
        const ventasMap = new Map();
        filteredChequesVentas.forEach(venta => {
          let impuestosCheque = 0;
          let netoCheque = 0;
          let descuentosCheque = 0;
          
          const pedidos = Array.isArray(venta.pedidos) ? venta.pedidos : [];
          pedidos.forEach(pedido => {
            const prods = Array.isArray(pedido.productos) ? pedido.productos : [];
            prods.forEach(producto => {
              if (producto.estado === 'void') return;
              
              const precio = parseFloat(producto.precio) || 0;
              const cantidad = parseFloat(producto.cantidad) || 0;
              const tasaImpuesto = (parseFloat(producto.impuesto) || 0) / 100;
              const descuentoProducto = parseFloat(producto.monto_descuento) || 0;
              
              let totalModificadores = 0;
              let descuentoModificadores = 0;
              const modificadores = Array.isArray(producto.modificadores) ? producto.modificadores : [];
              
              modificadores.forEach(mod => {
                totalModificadores += (parseFloat(mod.precio) || 0) * (parseFloat(mod.cantidad) || 1);
                descuentoModificadores += parseFloat(mod.monto_descuento) || 0;
              });
              
              const ventaSinDescuento = precio * cantidad + totalModificadores;
              const descuentoTotal = descuentoProducto + descuentoModificadores;
              const totalConImpuestos = ventaSinDescuento - descuentoTotal;
              
              descuentosCheque += descuentoTotal;
              
              if (tasaImpuesto > 0) {
                const netoProducto = totalConImpuestos / (1 + tasaImpuesto);
                const impuestoProducto = totalConImpuestos - netoProducto;
                netoCheque += netoProducto;
                impuestosCheque += impuestoProducto;
              } else {
                netoCheque += totalConImpuestos;
              }
            });
          });
          
          ventasMap.set(venta.id, {
            id: venta.id,
            revenue: venta.revenue || 'Sin Revenue',
            neto: netoCheque,
            impuestos: impuestosCheque,
            descuentos: descuentosCheque,
            total: parseFloat(venta.total_cheque) || 0,
            cliente: venta.cliente || 'Consumidor Final'
          });
        });

        // Procesar cheques de corte
        filteredChequesCorte.forEach(cheque => {
          const importe = parseFloat(cheque.total_cheque) || 0;
          totalVentas += importe;
          sumaCheques += importe;
          
          const ventaData = ventasMap.get(cheque.id);
          const revenue = ventaData?.revenue || 'Sin Revenue';
          const cliente = ventaData?.cliente || cheque.cliente || 'Consumidor Final';
          
          // Contar clientes únicos
          if (cliente && cliente !== 'Consumidor Final' && cliente !== 'Sin nombre') {
            if (!clientesMap.has(cliente)) {
              clientesMap.set(cliente, 0);
            }
            clientesMap.set(cliente, clientesMap.get(cliente) + importe);
          } else if (cliente === 'Consumidor Final') {
            // También contar consumidores finales como clientes (pueden ser varios)
            if (!clientesMap.has('Consumidor Final')) {
              clientesMap.set('Consumidor Final', 0);
            }
            clientesMap.set('Consumidor Final', clientesMap.get('Consumidor Final') + importe);
          }
          
          // Procesar por revenue
          if (!revenueMap.has(revenue)) {
            revenueMap.set(revenue, {
              totalVentas: 0,
              totalCheques: 0,
              totalImpuestos: 0,
              totalDescuentos: 0,
              totalSinImpuestos: 0,
              cheques: []
            });
          }
          
          const revenueData = revenueMap.get(revenue);
          revenueData.totalVentas += importe;
          revenueData.totalCheques += 1;
          
          if (ventaData) {
            revenueData.totalImpuestos += ventaData.impuestos;
            revenueData.totalDescuentos += ventaData.descuentos;
            revenueData.totalSinImpuestos += ventaData.neto;
          } else {
            revenueData.totalSinImpuestos += importe;
          }
          revenueData.cheques.push({
            id: cheque.id,
            total: importe,
            cliente: cliente,
            vendedor: cheque.nombre_vendedor
          });
          
          if (ventaData) {
            totalSinImpuestos += ventaData.neto;
            totalImpuestos += ventaData.impuestos;
            totalDescuentos += ventaData.descuentos;
          } else {
            totalSinImpuestos += importe;
          }
          
          // Procesar pagos
          const pagos = Array.isArray(cheque.pagos) ? cheque.pagos : [];
          let propinasCheque = 0;
          let cambioCheque = 0;
          
          pagos.forEach(pago => {
            const montoTotal = parseFloat(pago.monto_total) || 0;
            const montoPropina = parseFloat(pago.monto_propina) || 0;
            const montoCambio = parseFloat(pago.monto_cambio) || 0;
            const paymentMethod = pago.nombre_pago || 'Desconocido';
            
            propinasCheque += montoPropina;
            cambioCheque += montoCambio;
            totalCobros += montoTotal;
            
            if (!paymentMethods[paymentMethod]) {
              paymentMethods[paymentMethod] = { cobros: 0, propinas: 0, count: 0 };
            }
            paymentMethods[paymentMethod].cobros += montoTotal;
            paymentMethods[paymentMethod].propinas += montoPropina;
            paymentMethods[paymentMethod].count += 1;
          });
          
          totalPropinas += propinasCheque;
          totalCambio += cambioCheque;
          
          const totalPayments = pagos.reduce((sum, pago) => {
            return sum + ((parseFloat(pago.monto_total) || 0) - 
                         (parseFloat(pago.monto_propina) || 0) - 
                         (parseFloat(pago.monto_cambio) || 0));
          }, 0);
          const fueraDeBalance = importe - totalPayments;
          totalFueraDeBalance += fueraDeBalance;
          
          if (Math.abs(fueraDeBalance) >= 0.01) {
            unbalancedCheques.push({
              id: cheque.id,
              fueraDeBalance,
              nombre_vendedor: cheque.nombre_vendedor
            });
          }
        });

        totalClientes = clientesMap.size;
        const chequePromedio = filteredChequesCorte.length > 0 ? sumaCheques / filteredChequesCorte.length : 0;
        
        // Convertir revenueMap a array y ordenar por ventas
        const revenueArray = Array.from(revenueMap.entries())
          .map(([name, data]) => ({
            name,
            ...data
          }))
          .sort((a, b) => b.totalVentas - a.totalVentas);
        
        // Requerimiento: se reemplaza "TOP 5 CLIENTES" (agrupaba por el
        // campo "cliente", que en realidad es el asiento dentro de la
        // cuenta en mesas normales y el número de comensales en Banquetes
        // — no identificaba clientes reales) por "TOP 5 CHEQUES DEL DÍA":
        // las 5 cuentas de mayor monto del día, con su revenue y mesero,
        // que sí es información real y verificable.
        const topCheques = [...filteredChequesCorte]
          .sort((a, b) => (parseFloat(b.total_cheque) || 0) - (parseFloat(a.total_cheque) || 0))
          .slice(0, 5)
          .map(cheque => ({
            numeroCheque: cheque.numero_cheque || cheque.id,
            revenue: cheque.revenue || 'Sin Revenue',
            mesero: cheque.nombre_vendedor || 'Sin vendedor',
            mesa: cheque.mesa || '',
            total: parseFloat(cheque.total_cheque) || 0,
          }));

        // Requerimiento: porcentaje de variación de la venta de hoy contra
        // ayer. null cuando ayer no tiene ventas registradas (evita dividir
        // entre cero / mostrar un porcentaje sin sentido).
        const porcentajeVsAyer = totalVentasAyer > 0
          ? ((totalVentas - totalVentasAyer) / totalVentasAyer) * 100
          : null;

        const datosProcesados = {
          totals: {
            totalVentas,
            totalSinImpuestos,
            totalImpuestos,
            totalCobros,
            totalPropinas,
            totalCambio,
            totalDescuentos,
            totalFueraDeBalance,
            totalCheques: filteredChequesCorte.length,
            unbalancedCheques,
            chequePromedio,
            totalClientes
          },
          paymentMethods,
          revenue: revenueArray,
          topCheques,
          comparativoAyer: {
            totalHoy: totalVentas,
            totalAyer: totalVentasAyer,
            porcentaje: porcentajeVsAyer,
          }
        };

        console.log("✅ Datos procesados:", datosProcesados);
        setDatosCorte(datosProcesados);
      }
    } catch (error) {
      console.error("Error cargando datos:", error);
      setDatosCorte(null);
    }
  };

  const formatMoney = (value) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value || 0);
  };

  const cargarConfiguracionWhatsApp = async () => {
    try {
      const response = await fetch(`${IP}/tavpos/backend/data/${encodeURIComponent(property)}/config.json`);
      if (!response.ok) return [];
      const configData = await response.json();
      return configData.whatsapp?.map(item => item.cel) || [];
    } catch (error) {
      console.error("Error:", error);
      return [];
    }
  };

  const enviarWhatsAppAutomatico = async (fechaCierreFormateada, numerosDestino) => {
    if (numerosDestino.length === 0) {
      return { success: false, message: "No hay números configurados" };
    }

    const fechaHoraCierre = new Date().toLocaleString('es-MX', {
      timeZone: 'America/Mexico_City',
      dateStyle: 'short',
      timeStyle: 'medium'
    });

    // Requerimiento: el mensaje debe reflejar quién hizo el cierre. Como
    // ControlFecha no recibe currentUser por props, se lee del mismo
    // localStorage donde App.js lo guarda (clave "currentUser", campo
    // "nombre") — igual criterio que eod-service, que marca
    // "no fue un usuario" cuando el cierre es automático.
    let nombreUsuarioCierre = "Usuario desconocido";
    try {
      const savedUser = localStorage.getItem("currentUser");
      if (savedUser) {
        const parsedUser = JSON.parse(savedUser);
        if (parsedUser?.nombre) nombreUsuarioCierre = parsedUser.nombre;
      }
    } catch (error) {
      console.error('Error al leer currentUser para el mensaje de WhatsApp:', error);
    }

    let mensaje = `✅ *CIERRE DIARIO EXITOSO*\n\n`;
    mensaje += `📱 *TAV POS*\n\n`;
    mensaje += `📅 *Fecha Cerrada:* ${fechaCierreFormateada}\n`;
    mensaje += `🏨 *Propiedad:* ${property}\n`;
    mensaje += `⏰ *Fecha y Hora de Cierre:* ${fechaHoraCierre}\n`;
    mensaje += `🤖 *Origen:* ${nombreUsuarioCierre}\n\n`;

    if (datosCorte && datosCorte.totals && datosCorte.totals.totalCheques > 0) {
      const totalVentasAntesDescuentos = datosCorte.totals.totalVentas + datosCorte.totals.totalDescuentos;
      const porcentajeDescuentos = totalVentasAntesDescuentos > 0 
        ? ((datosCorte.totals.totalDescuentos / totalVentasAntesDescuentos) * 100).toFixed(2)
        : '0.00';
      const totalEnCajaNeto = datosCorte.totals.totalCobros - (datosCorte.totals.totalPropinas + datosCorte.totals.totalCambio);

      mensaje += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
      mensaje += `📊 *RESUMEN DE VENTAS*\n`;
      mensaje += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
      mensaje += `💰 Ventas Brutas: ${formatMoney(totalVentasAntesDescuentos)}\n`;
      mensaje += `🏷️ Descuentos: ${formatMoney(datosCorte.totals.totalDescuentos)} (${porcentajeDescuentos}%)\n`;
      mensaje += `✨ Ventas Netas: ${formatMoney(datosCorte.totals.totalVentas)}\n`;
      mensaje += `📊 Ventas sin Impuestos: ${formatMoney(datosCorte.totals.totalSinImpuestos)}\n`;
      mensaje += `🧾 Impuestos: ${formatMoney(datosCorte.totals.totalImpuestos)}\n\n`;

      // Requerimiento: comparación de la venta de hoy contra la de ayer.
      if (datosCorte.comparativoAyer) {
        const { totalHoy, totalAyer, porcentaje } = datosCorte.comparativoAyer;
        mensaje += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
        mensaje += `📈 *COMPARACIÓN CON AYER*\n`;
        mensaje += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
        mensaje += `Hoy: ${formatMoney(totalHoy)} · Ayer: ${formatMoney(totalAyer)}\n`;
        if (porcentaje === null) {
          mensaje += `ℹ️ No hay venta registrada ayer para comparar.\n\n`;
        } else if (porcentaje >= 0) {
          mensaje += `🔼 ${porcentaje.toFixed(1)}% más que ayer\n\n`;
        } else {
          mensaje += `🔽 ${Math.abs(porcentaje).toFixed(1)}% menos que ayer\n\n`;
        }
      }

      mensaje += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
      mensaje += `💵 *RESUMEN DE COBROS*\n`;
      mensaje += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
      mensaje += `💳 Total Cobrado: ${formatMoney(datosCorte.totals.totalCobros)}\n`;
      mensaje += `💸 Cambio Entregado: ${formatMoney(datosCorte.totals.totalCambio)}\n`;
      mensaje += `💎 Propinas: ${formatMoney(datosCorte.totals.totalPropinas)}\n`;
      mensaje += `💰 *Total en Caja:* ${formatMoney(totalEnCajaNeto)}\n\n`;
      
      // NUEVA SECCIÓN: ESTADÍSTICAS DE CLIENTES Y CHEQUES
      mensaje += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
      mensaje += `👥 *ESTADÍSTICAS DE CLIENTES*\n`;
      mensaje += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
      mensaje += `👤 Total Clientes Atendidos: ${datosCorte.totals.totalClientes}\n`;
      mensaje += `📄 Total Cheques Procesados: ${datosCorte.totals.totalCheques}\n`;
      mensaje += `💰 Cheque Promedio: ${formatMoney(datosCorte.totals.chequePromedio)}\n\n`;
      
      // Top 5 cheques del día (reemplaza "TOP 5 CLIENTES")
      if (datosCorte.topCheques && datosCorte.topCheques.length > 0) {
        mensaje += `🏆 *TOP 5 CHEQUES DEL DÍA*\n`;
        datosCorte.topCheques.forEach((cheque, idx) => {
          const mesaTxt = cheque.mesa ? ` (${cheque.mesa})` : '';
          mensaje += `▸ ${idx + 1}. Cheque #${cheque.numeroCheque} — ${cheque.revenue}${mesaTxt} — Mesero: ${cheque.mesero}: ${formatMoney(cheque.total)}\n`;
        });
        mensaje += `\n`;
      }
      
      // NUEVA SECCIÓN: VENTAS POR REVENUE (Centro de Ingresos)
      if (datosCorte.revenue && datosCorte.revenue.length > 0) {
        mensaje += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
        mensaje += `🏪 *VENTAS POR CENTRO DE INGRESOS*\n`;
        mensaje += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
        
        datosCorte.revenue.forEach(rev => {
          const porcentaje = (rev.totalVentas / datosCorte.totals.totalVentas) * 100;
          mensaje += `📌 *${rev.name}*\n`;
          mensaje += `   💰 Total: ${formatMoney(rev.totalVentas)} (${porcentaje.toFixed(1)}%)\n`;
          mensaje += `   📋 Cheques: ${rev.totalCheques}\n`;
          mensaje += `   📊 Promedio por cheque: ${formatMoney(rev.totalVentas / rev.totalCheques)}\n`;
          if (rev.totalImpuestos > 0) {
            mensaje += `   🧾 Impuestos: ${formatMoney(rev.totalImpuestos)}\n`;
          }
          mensaje += `\n`;
        });
      }
      
      // Métodos de pago
      if (datosCorte.paymentMethods && Object.keys(datosCorte.paymentMethods).length > 0) {
        mensaje += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
        mensaje += `💳 *MÉTODOS DE PAGO*\n`;
        mensaje += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
        
        Object.entries(datosCorte.paymentMethods).forEach(([metodo, detalles]) => {
          const porcentaje = datosCorte.totals.totalCobros > 0 
            ? ((detalles.cobros / datosCorte.totals.totalCobros) * 100).toFixed(1)
            : '0.0';
          mensaje += `▸ ${metodo}: ${formatMoney(detalles.cobros)} (${porcentaje}%)\n`;
        });
        mensaje += `\n`;
      }
      
      // Cheques fuera de balance
      if (datosCorte.totals.unbalancedCheques && datosCorte.totals.unbalancedCheques.length > 0) {
        mensaje += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
        mensaje += `⚠️ *CHEQUES FUERA DE BALANCE*\n`;
        mensaje += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
        mensaje += `📋 Total: ${datosCorte.totals.unbalancedCheques.length} cheque(s)\n`;
        mensaje += `💰 Monto total: ${formatMoney(datosCorte.totals.totalFueraDeBalance)}\n\n`;
        mensaje += `🔍 *Cheques con desbalance:*\n`;
        datosCorte.totals.unbalancedCheques.slice(0, 5).forEach(cheque => {
          mensaje += `▸ Cheque ${cheque.id}: ${formatMoney(cheque.fueraDeBalance)} (${cheque.nombre_vendedor})\n`;
        });
        if (datosCorte.totals.unbalancedCheques.length > 5) {
          mensaje += `▸ ... y ${datosCorte.totals.unbalancedCheques.length - 5} más\n`;
        }
        mensaje += `\n`;
      }
      
    } else {
      mensaje += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
      mensaje += `ℹ️ *SIN VENTAS REGISTRADAS*\n`;
      mensaje += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
      mensaje += `No se encontraron ventas para la fecha ${fechaCierreFormateada}\n\n`;
    }
    
    mensaje += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
    mensaje += `🔒 *Todas las operaciones para esta fecha han sido finalizadas.*\n`;
    mensaje += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
    mensaje += `\n📱 *TAV POS* - Sistema de Gestión\n`;
    mensaje += `🕒 Reporte generado automáticamente\n`;

    try {
      console.log("🔧 Enviando WhatsApp...");
      
      setProgresoWhatsApp({
        total: numerosDestino.length,
        enviados: 0,
        fallidos: 0,
        actual: 'Iniciando envío...',
        estado: 'iniciando'
      });

      setProgresoWhatsApp(prev => ({
        ...prev,
        actual: 'Preparando envío masivo...',
        estado: 'enviando'
      }));

      const response = await fetch(`${IP}/tavpos/backend/enviar_whatsapp.php`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({
          numero: numerosDestino,
          mensaje: mensaje,
          property: property,
          tipo: 'cierre_diario',
          datos_resumen: datosCorte ? {
            total_ventas: datosCorte.totals.totalVentas,
            total_cobros: datosCorte.totals.totalCobros,
            total_propinas: datosCorte.totals.totalPropinas,
            total_cheques: datosCorte.totals.totalCheques,
            cheque_promedio: datosCorte.totals.chequePromedio,
            total_clientes: datosCorte.totals.totalClientes,
            fecha: fechaCierreFormateada
          } : null
        })
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const result = await response.json();
      
      const exitosos = result.total_enviados || 0;
      const fallidos = result.total_fallidos || 0;
      
      setProgresoWhatsApp({
        total: numerosDestino.length,
        enviados: exitosos,
        fallidos: fallidos,
        actual: 'Procesamiento completado',
        estado: 'completado'
      });

      console.log(`📱 WhatsApp: ${exitosos}/${numerosDestino.length} mensajes enviados`);
      
      return {
        success: result.success,
        total: numerosDestino.length,
        exitosos: exitosos,
        fallidos: fallidos,
        detalles: result.resultados || [],
        method: 'textmebot'
      };
      
    } catch (error) {
      console.error("Error:", error);
      setProgresoWhatsApp(prev => ({
        ...prev,
        estado: 'error',
        actual: `Error: ${error.message}`
      }));
      return { success: false, error: error.message };
    }
  };

  const RenderProgresoWhatsApp = () => {
    if (!enviandoWhatsApp) return null;

    const { total, enviados, fallidos, actual, estado } = progresoWhatsApp;
    const porcentaje = total > 0 ? Math.round((enviados / total) * 100) : 0;

    return (
      <div className="whatsapp-progress-overlay">
        <div className="whatsapp-progress-container">
          <div className="whatsapp-progress-header">
            <h3>📱 Enviando Notificaciones por WhatsApp</h3>
            <div className="whatsapp-progress-stats">
              <span>✅ {enviados} enviados</span>
              <span>❌ {fallidos} fallidos</span>
              <span>📋 {total} total</span>
            </div>
          </div>
          
          <div className="whatsapp-progress-bar-container">
            <div className="whatsapp-progress-bar" style={{ width: `${porcentaje}%` }}></div>
          </div>
          
          <div className="whatsapp-progress-text">
            {estado === 'iniciando' && (
              <div className="progress-status">
                <div className="spinner-small"></div>
                <span>Iniciando envío de notificaciones...</span>
              </div>
            )}
            
            {estado === 'enviando' && (
              <div className="progress-status">
                <div className="spinner-small"></div>
                <span>Enviando mensajes... {actual}</span>
              </div>
            )}
            
            {estado === 'completado' && (
              <div className="progress-status completed">
                <span>✅ {actual}</span>
                <span>{enviados} de {total} mensajes enviados exitosamente</span>
              </div>
            )}
            
            {estado === 'error' && (
              <div className="progress-status error">
                <span>❌ {actual}</span>
              </div>
            )}
          </div>

          <div className="whatsapp-progress-note">
            ⏰ Este proceso puede tomar varios segundos. Por favor espere...
          </div>
        </div>
      </div>
    );
  };

  const RenderProgresoBackup = () => {
    if (!realizandoBackup) return null;

    return (
      <div className="whatsapp-progress-overlay">
        <div className="whatsapp-progress-container backup-progress-container">
          <div className="whatsapp-progress-header">
            <h3>💾 Realizando Respaldo de Base de Datos</h3>
          </div>
          
          <div className="whatsapp-progress-bar-container">
            <div className="whatsapp-progress-bar backup-progress"></div>
          </div>
          
          <div className="whatsapp-progress-text">
            <div className="progress-status">
              <div className="spinner-small"></div>
              <span>Creando respaldo de seguridad...</span>
            </div>
          </div>

          <div className="whatsapp-progress-note">
            ⏰ Creando copia de seguridad de la base de datos. Por favor espere...
          </div>
        </div>
      </div>
    );
  };

  const handleCierreDia = async () => {
    if (!property || !fechaControl?.fecha_trabajo) {
      setError("No se puede determinar la fecha para el cierre.");
      return;
    }

    const fechaTrabajo = new Date(fechaControl.fecha_trabajo);
    const fechaCierre = addDays(fechaTrabajo, 1);
    const fechaCierreFormateada = format(fechaCierre, FORMATO_FECHA);
    const fechaCierreArchivo = format(fechaCierre, "yyyy-MM-dd");

    const confirmar = window.confirm(
      `🔒 CONFIRMAR CIERRE DIARIO\n\n` +
      `¿Está seguro que desea realizar el cierre diario?\n\n` +
      `📅 Fecha a cerrar: ${fechaCierreFormateada}\n\n` +
      `Una vez confirmado, no podrá realizar más operaciones para esta fecha.\n\n` +
      `¿Desea continuar?`
    );

    if (!confirmar) return;

    try {
      setLoading(true);
      const fechaLocal = new Date().toISOString();

      const response = await fetch(
        `${IP}/tavpos/backend/close_day_cierre.php?property=${encodeURIComponent(property)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ fecha_cierre: fechaLocal, user_id: 1 }),
        }
      );

      if (!response.ok) throw new Error(`Error al realizar cierre diario`);
      const data = await response.json();
      if (!data.success) throw new Error(data.message);

      const numerosWhatsApp = await cargarConfiguracionWhatsApp();
      
      let resultadoWhatsApp = { success: false, exitosos: 0 };
      if (numerosWhatsApp.length > 0) {
        setEnviandoWhatsApp(true);
        resultadoWhatsApp = await enviarWhatsAppAutomatico(fechaCierreFormateada, numerosWhatsApp);
        await new Promise(resolve => setTimeout(resolve, 2000));
        setEnviandoWhatsApp(false);
      }

      let resultadoBackup = { success: false };
      try {
        setRealizandoBackup(true);
        const backupResponse = await fetch(`${IP}/tavpos/backend/backup_db.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ property, forzar: true, fecha_cierre: fechaCierreArchivo })
        });
        if (backupResponse.ok) {
          resultadoBackup = await backupResponse.json();
        }
      } catch (backupError) {
        console.error("Error en respaldo:", backupError);
      } finally {
        setRealizandoBackup(false);
      }

      let mensajeExito = `✅ CIERRE DIARIO EXITOSO\n\n📅 Fecha cerrada: ${fechaCierreFormateada}\n\n`;
      
      if (numerosWhatsApp.length > 0) {
        mensajeExito += `📱 WhatsApp: ${resultadoWhatsApp.exitosos}/${numerosWhatsApp.length} enviados\n`;
      }
      
      if (resultadoBackup.success) {
        mensajeExito += `💾 Respaldo: ${resultadoBackup.archivo_respaldo}\n`;
      }
      
      mensajeExito += `\n🔒 Operaciones finalizadas. El sistema se recargará.`;
      alert(mensajeExito);
      
      setTimeout(() => window.location.reload(), 3000);
      
    } catch (error) {
      console.error("Error:", error);
      setEnviandoWhatsApp(false);
      setRealizandoBackup(false);
      alert(`❌ ERROR: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Cargando datos...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="error-container">
          <h3>⚠️ Error</h3>
          <p>{error}</p>
          <button className="retry-button" onClick={() => window.location.reload()}>
            Reintentar
          </button>
        </div>
      );
    }

    if (!fechaControl) {
      return (
        <div className="empty-message">
          ⚠️ No se encontró información de fecha.
        </div>
      );
    }

    const fechaActual = new Date();
    const fechaTrabajo = fechaControl.fecha_trabajo
      ? format(new Date(fechaControl.fecha_trabajo), FORMATO_FECHA)
      : "No disponible";

    const fechaTrabajoDate = fechaControl.fecha_trabajo ? new Date(fechaControl.fecha_trabajo) : null;
    const diferenciaDias = fechaTrabajoDate
      ? Math.floor((fechaActual - fechaTrabajoDate) / (1000 * 60 * 60 * 24))
      : 0;

    return (
      <>
        <div className={`fecha-control ${diferenciaDias > 0 ? "rojo" : "verde"}`}>
          <div className="fecha-header">
            <span>🗓️ Control de Fechas</span>
            {diferenciaDias > 0 && (
              <span className="diferencia-dias">
                {diferenciaDias} día{diferenciaDias > 1 ? "s" : ""} de diferencia
              </span>
            )}
          </div>
          <div className="fecha-detalle">
            <span>Fecha Actual: </span>
            <strong>{format(fechaActual, FORMATO_FECHA)}</strong>
          </div>
          <div className="fecha-detalle">
            <span>Último Cierre: </span>
            <strong>{fechaTrabajo}</strong>
          </div>
        </div>

        <div className="cheques-container">
          <h3>📋 Cheques Abiertos ({cheques.length})</h3>
          {cheques.length === 0 ? (
            <div className="empty-message">🎉 ¡No hay cheques abiertos!</div>
          ) : (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th># Cheque</th>
                    <th>Mesa</th>
                    <th>Cliente</th>
                    <th>Vendedor</th>
                    <th>Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {cheques.map((cheque) => (
                    <tr key={cheque.numero_cheque} onClick={() => onChequeSeleccionado(cheque)}>
                      <td>{cheque.numero_cheque}</td>
                      <td>{cheque.mesa}</td>
                      <td>{cheque.cliente || "Sin nombre"}</td>
                      <td>{cheque.nombre_vendedor}</td>
                      <td>
                        <button
                          className="select-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onChequeSeleccionado(cheque);
                            onClose();
                          }}
                        >
                          Seleccionar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </>
    );
  };

  if (!show) return null;

  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-container" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Control de Fechas y Cheques</h2>
            <button className="close-button" onClick={onClose} disabled={loading || enviandoWhatsApp || realizandoBackup}>
              ×
            </button>
          </div>

          <div className="modal-body">{renderContent()}</div>

          <div className="modal-footer">
            <button
              className="cierre-button"
              onClick={handleCierreDia}
              disabled={cheques.length > 0 || loading || enviandoWhatsApp || realizandoBackup}
              title={cheques.length > 0 ? "Cierre todos los cheques primero" : ""}
            >
              {loading ? "Procesando..." : 
               enviandoWhatsApp ? "Enviando WhatsApp..." : 
               realizandoBackup ? "Realizando respaldo..." : 
               "Realizar Cierre Diario"}
            </button>
            <button className="close-button" onClick={onClose} disabled={loading || enviandoWhatsApp || realizandoBackup}>
              Cerrar
            </button>
          </div>

          {cheques.length > 0 && (
            <div className="warning-message">
              ⚠️ No se puede realizar el cierre diario mientras haya cheques abiertos.
            </div>
          )}
        </div>
      </div>

      <RenderProgresoWhatsApp />
      <RenderProgresoBackup />
    </>
  );
};

ControlFecha.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onChequeSeleccionado: PropTypes.func.isRequired,
};

export default ControlFecha;